import { Cesium } from 'app/cesium';
import { AsyncPrimitive } from 'app/map/geometry/cesium/AsyncPrimitive/AsyncUpdatablePrimitive';
import { CesiumZone } from 'app/map/geometry/cesium/CesiumZones/CesiumZone';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { cartesian3ToDegrees, LatLon } from 'app/shared/utilities/mapping_utils';
import _ from 'lodash';

export const DegreesToMetersApprox = 1 / 111139;
export interface PolylineAsyncPrimitiveAttributes {
    show: boolean;
    width: number;
    color: Cesium.Color;
    positions: Array<Cesium.Cartesian3>;
    zIndex: number;
}

const isTripleEqual = (a, b) => a === b;

const isPositionEqual = (a, b) =>
    a === b ||
    (a instanceof Cesium.Cartesian3 &&
        b instanceof Cesium.Cartesian3 &&
        (a as Cesium.Cartesian3).equals(b as Cesium.Cartesian3));
const passThrough = <T>(v: T) => v;

export function createPolylineAsyncPrimitive(
    id: number,
    primitiveCollection: Cesium.PrimitiveCollection,
    allowSelection: boolean,
): AsyncPrimitive<PolylineAsyncPrimitiveAttributes> {
    return new AsyncPrimitive<PolylineAsyncPrimitiveAttributes>(
        primitiveCollection,
        {
            show: {
                requiresPrimitiveRecreation: false,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
            color: {
                requiresPrimitiveRecreation: false,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
            width: {
                requiresPrimitiveRecreation: true,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
            positions: {
                requiresPrimitiveRecreation: true,
                isEqual: isPositionsEqual,
                copy: (positions) => positions?.slice(0),
            },
            zIndex: {
                requiresPrimitiveRecreation: false,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
        },
        (prim, att) => {
            updatePrimitiveColorAndShow(prim, att.color, att.show);
            appendZIndex(prim, att.zIndex);
        },
        (attributes) =>
            createPolylinePrimitive(
                id,
                attributes.color,
                attributes.positions,
                allowSelection,
                SelectionType.ASSET,
                attributes.width,
                attributes.zIndex,
            ),
    );
}

export interface ImageOverlayAsyncPrimitiveAttributes {
    show: boolean;
    positions: Array<Cesium.Cartesian3>;
    labelPosition: Cesium.Cartesian3;
    image: string;
    color: Cesium.Color;
}

export function createImageOverlayAsyncPrimitive(
    id: number,
    primitiveCollection: Cesium.PrimitiveCollection,
): AsyncPrimitive<ImageOverlayAsyncPrimitiveAttributes> {
    return new AsyncPrimitive<ImageOverlayAsyncPrimitiveAttributes>(
        primitiveCollection,
        {
            show: {
                requiresPrimitiveRecreation: false,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
            positions: {
                requiresPrimitiveRecreation: true,
                isEqual: isPositionsEqual,
                copy: (positions) => positions?.slice(0),
            },
            labelPosition: {
                requiresPrimitiveRecreation: true,
                isEqual: isPositionEqual,
                copy: (position) => position.clone(),
            },
            image: {
                requiresPrimitiveRecreation: true,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
            color: {
                requiresPrimitiveRecreation: true,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
        },
        (prim, att) => {
            updatePrimitiveColorAndShow(prim, null, att.show);
            appendZIndex(prim, 1);
        },
        (attributes) =>
            createImageOverlayPrimitive(
                id,
                attributes.positions,
                attributes.labelPosition,
                attributes.image,
                attributes.color,
            ),
    );
}

export interface PolygonAsyncPrimitiveAttributes {
    show: boolean;
    color: Cesium.Color;
    positions: Array<Cesium.Cartesian3>;
    zIndex: number;
}

export function createPolygonAsyncPrimitive(
    id: number,
    primitiveCollection: Cesium.PrimitiveCollection,
    allowSelection: boolean,
    selectionType: SelectionType,
): AsyncPrimitive<PolygonAsyncPrimitiveAttributes> {
    return new AsyncPrimitive<PolygonAsyncPrimitiveAttributes>(
        primitiveCollection,
        {
            show: {
                requiresPrimitiveRecreation: false,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
            color: {
                requiresPrimitiveRecreation: false,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
            positions: {
                requiresPrimitiveRecreation: true,
                isEqual: isPositionsEqual,
                copy: (positions) => positions?.slice(0),
            },
            zIndex: {
                requiresPrimitiveRecreation: false,
                isEqual: isTripleEqual,
                copy: passThrough,
            },
        },
        (prim, att) => {
            updatePrimitiveColorAndShow(prim, att.color, att.show);
            appendZIndex(prim, att.zIndex);
        },
        (attributes) =>
            createPolygonPrimitive(
                id,
                attributes.color,
                attributes.positions,
                allowSelection,
                selectionType,
                attributes.zIndex,
            ),
    );
}

// a token id, used to fetch the attributes later
const GEOM_INSTANCE_ID = 'zone';

function createPolygonPrimitive(
    zoneId: number,
    color: Cesium.Color,
    positions: Array<Cesium.Cartesian3>,
    allowSelection: boolean = true,
    selectionType: SelectionType,
    zIndex: number,
): Cesium.Primitive {
    const polygonGeometry = Cesium.PolygonGeometry.fromPositions({
        positions: positions,
        extrudedHeight: undefined, // no extrusion
        height: 0, // no height - shouldn't matter, we'll use a ground primitive to display this geometry anyway
        vertexFormat: Cesium.PerInstanceColorAppearance.VERTEX_FORMAT,
        perPositionHeight: false, // the positions array shouldn't need a height value (it's just latitude and longitude)
        stRotation: null,
        ellipsoid: null,
        granularity: null,
        closeTop: null,
        closeBottom: null,
        arcType: null,
        textureCoordinates: null,
    });
    const geometryInstance = createGeometryInstance(polygonGeometry, color);
    const primitive = new Cesium.GroundPrimitive(
        getGroundPrimitiveAttributes(
            geometryInstance,
            new Cesium.PerInstanceColorAppearance({
                closed: true,
            }),
        ),
    );
    smashOnSelectionProperties(allowSelection, primitive, zoneId, selectionType, true);
    appendZIndex(primitive, zIndex);
    return primitive;
}

function createPolylinePrimitive(
    zoneId: number,
    color: Cesium.Color,
    positions: Array<Cesium.Cartesian3>,
    allowSelection: boolean = true,
    selectionType: SelectionType,
    width: number,
    zIndex: number,
): Cesium.Primitive {
    const polygonGeometry = new Cesium.GroundPolylineGeometry({
        positions,
        width,
        loop: false,
    });
    const geometryInstance = createGeometryInstance(polygonGeometry, color);
    const primitive = new Cesium.GroundPolylinePrimitive(
        getGroundPrimitiveAttributes(
            geometryInstance,
            new Cesium.PolylineColorAppearance({
                closed: false,
            }),
        ),
    );
    smashOnSelectionProperties(allowSelection, primitive, zoneId, selectionType);
    appendZIndex(primitive, zIndex);
    return primitive;
}

/** @deprecated */
function createImageOverlayPrimitive(
    zoneId: number,
    positions: Array<Cesium.Cartesian3>,
    labelPosition: Cesium.Cartesian3,
    image: string,
    color: Cesium.Color,
): Cesium.Primitive {
    const center = cartesian3ToDegrees(labelPosition);

    let offset =
        calculateSmallestAxisDistance(positions, center.latitude, center.longitude) *
        0.2 *
        DegreesToMetersApprox; // 20% * degrees to meters conversion

    // the biggest the overlays can get is 100m * 100m
    offset = Math.min(offset, 50 * DegreesToMetersApprox);

    // longitude gets smaller as we get closer to a pole
    let longitudeScaleFactor = Math.cos(Cesium.Math.toRadians(center.latitude));
    longitudeScaleFactor = Math.max(0.01, longitudeScaleFactor); // we don't want to divide by 0
    const widthScale = 1 / longitudeScaleFactor;

    const geometryInstance = new Cesium.GeometryInstance({
        geometry: new Cesium.RectangleGeometry({
            rectangle: Cesium.Rectangle.fromDegrees(
                center.longitude - offset * widthScale,
                center.latitude - offset,
                center.longitude + offset * widthScale,
                center.latitude + offset,
            ),
        }),
        id: 'GEOM_INSTANCE_ID',
    });

    const primitive = new Cesium.GroundPrimitive(
        getGroundPrimitiveAttributes(
            geometryInstance,
            new Cesium.MaterialAppearance({
                material: new Cesium.Material({
                    fabric: {
                        type: 'Image',
                        uniforms: {
                            image,
                            color,
                        },
                    },
                }),
            }),
        ),
    );
    appendZIndex(primitive, 0);
    return primitive;
}

// For a given polygon and a center point, calculate the length of the line that runs east-west
// from the center point and intersects the polygon. Do the same for north-south, then return
// the smaller of those two lengths.
function calculateSmallestAxisDistance(
    polygonPoints: Cesium.Cartesian3[],
    lat: number,
    lon: number,
) {
    const intersectionsLon: LatLon[] = [];
    const intersectionsLat: LatLon[] = [];

    const points: LatLon[] = polygonPoints.map((i) => cartesian3ToDegrees(i));

    // Iterate over each edge of the polygon
    for (let i = 0; i < points.length; i++) {
        const p1 = points[i];
        const p2 = points[(i + 1) % points.length];

        // Check if the edge intersects with the x-coordinate
        if (
            (p1.latitude >= lat && p2.latitude < lat) ||
            (p1.latitude < lat && p2.latitude >= lat)
        ) {
            const t = (lat - p1.latitude) / (p2.latitude - p1.latitude);
            const y = p1.longitude + t * (p2.longitude - p1.longitude);
            intersectionsLon.push({ latitude: lat, longitude: y });
        }

        // Check if the edge intersects with the y-coordinate
        if (
            (p1.longitude >= lon && p2.longitude < lon) ||
            (p1.longitude < lon && p2.longitude >= lon)
        ) {
            const t = (lon - p1.longitude) / (p2.longitude - p1.longitude);
            const x = p1.latitude + t * (p2.latitude - p1.latitude);
            intersectionsLat.push({ latitude: x, longitude: lon });
        }
    }

    // sort the intersections points by distance to the center
    const sortedLon: LatLon[] = _.sortBy(intersectionsLon, (i: LatLon) =>
        Math.abs(lat - i.longitude),
    );

    const sortedLat: LatLon[] = _.sortBy(intersectionsLat, (i: LatLon) =>
        Math.abs(lat - i.latitude),
    );

    // get the first two intersection points
    const latPoints = sortedLat.slice(0, 2);
    const lonPoints = sortedLon.slice(0, 2);

    const distances: number[] = [];

    // return the smallest distance between intersection points
    distances.push(
        Cesium.Cartesian3.distance(
            new Cesium.Cartesian3.fromDegrees(latPoints[0].longitude, latPoints[0].latitude),
            new Cesium.Cartesian3.fromDegrees(latPoints[1].longitude, latPoints[1].latitude),
        ),
    );
    distances.push(
        Cesium.Cartesian3.distance(
            new Cesium.Cartesian3.fromDegrees(lonPoints[0].longitude, lonPoints[0].latitude),
            new Cesium.Cartesian3.fromDegrees(lonPoints[1].longitude, lonPoints[1].latitude),
        ),
    );

    return _.min(distances);
}

function smashOnSelectionProperties(
    allowSelection: boolean,
    primitive: Cesium.Primitive,
    objId: number,
    selectionType: SelectionType,
    isPolygon?: boolean,
) {
    // Identifiers for the SelectionService - not part of Cesium; we just jam them on here for later use
    if (allowSelection) {
        (primitive as any).getId = () => objId;
        (primitive as any).selectionType = selectionType;
        (primitive as any).isPolygon = !!isPolygon;
    }
}

function appendZIndex(primitive: Cesium.Primitive, zIndex: number): void {
    (primitive as any).zIndex = zIndex;
}

function getGroundPrimitiveAttributes(geometryInstance: Cesium.GeometryInstance, appearance) {
    return {
        // please store the geometry instance for us - we'll grab it later to update the color
        releaseGeometryInstances: false,
        geometryInstances: geometryInstance,
        // use the color attribute of the geometry - which we can update
        appearance,
        // start it off as hidden - and we'll show it (maybe) once it's ready to be shown
        //  this lets us swap out the previous primitive with this new primitive in a single frame
        show: false,
        // do things in a worker thread to not block the main thread
        //  meaning we have to wait for the ready promise before this primitive is rendered
        asynchronous: true,
    };
}

function createGeometryInstance(
    geom: Cesium.Geometry,
    color: Cesium.Color,
): Cesium.GeometryInstance {
    return new Cesium.GeometryInstance({
        // there's only 1 instance per primitive currently - so we'll give it a token id here.
        //  it does need the id though because we look it up later using special Cesium funcitons
        id: GEOM_INSTANCE_ID,
        geometry: geom,
        attributes: {
            color: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
        },
    });
}

function updatePrimitiveColorAndShow(
    primitive: Cesium.Primitive,
    color: Cesium.Color,
    show: boolean,
): void {
    const attributes = primitive.getGeometryInstanceAttributes(GEOM_INSTANCE_ID);
    if (!!color) {
        attributes.color = Cesium.ColorGeometryInstanceAttribute.toValue(color);
    }
    primitive.show = show;
}

function isPositionsEqual(
    prev: Array<Cesium.Cartesian3>,
    current: Array<Cesium.Cartesian3>,
): boolean {
    const isPositionGone = !!prev && !current;
    const isPositionAppearing = !prev && !!current;
    const isPositionChange =
        isPositionAppearing || isPositionGone || CesiumZone.isPositionsNotEqual(current, prev);
    return !isPositionChange;
}
